import { useEffect } from "react";
import { skipToken, useQuery, useQueryClient } from "@tanstack/react-query";
import { ErrorFromResponse, type Event } from "stream-chat";
import { useChatTokenQuery } from "Api/Queries/Chat/useChatTokenQuery";
import { useLoggedInUser } from "Hooks/useLoggedInUser";
import { useStreamChatClient } from "Hooks/Chat/useStreamChatClient";
import { isAuthenticated } from "Utils/AuthUtils";

const UNREAD_CHAT_COUNT_KEY = "UNREAD_CHAT_COUNT";

const unreadChatCountKeys = {
  all: [UNREAD_CHAT_COUNT_KEY],
  byStreamUser: (streamUserId?: string) => [
    ...unreadChatCountKeys.all,
    streamUserId,
  ],
};

export const useUnreadChatCount = () => {
  const user = useLoggedInUser();
  const isUserAuthenticated = isAuthenticated(user);
  const { data: chatToken } = useChatTokenQuery(
    isUserAuthenticated,
    user?.activeContextPublicID,
  );
  // The shared per-persona client — the same connection the chat page uses, so the badge adds no
  // second WebSocket; persona switches reconnect via the hook's query key.
  const { client } = useStreamChatClient(chatToken);
  const streamUserId = chatToken?.userId;
  const queryClient = useQueryClient();

  const { data: unreadCount = 0 } = useQuery<number>({
    queryKey: unreadChatCountKeys.byStreamUser(streamUserId),
    queryFn: client
      ? async () => {
          const response = await client.getUnreadCount();
          return response.total_unread_count;
        }
      : skipToken,
    // Badge fetch in the always-mounted menu — on failure the badge just shows 0. Nothing toasts
    // from the query cache; a Stream failure here is a non-API throw, so reportApiError captures
    // it grouped by stack — the earliest signal of a Stream outage. The default retry predicate
    // only understands API responses, so retry transient Stream failures explicitly: a Stream
    // 4xx (revoked token, deactivated user) is an outcome a retry will not change.
    retry: (failureCount, error) =>
      !(error instanceof ErrorFromResponse && error.status < 500) &&
      failureCount < 2,
  });

  // Subscribe to real-time unread count updates
  useEffect(() => {
    if (!client || !streamUserId) {
      return;
    }

    const handleEvent = ({ total_unread_count }: Event) => {
      if (total_unread_count !== undefined) {
        queryClient.setQueryData(
          unreadChatCountKeys.byStreamUser(streamUserId),
          total_unread_count,
        );
      }
    };

    client.on(handleEvent);

    return () => {
      client.off(handleEvent);
    };
  }, [client, streamUserId, queryClient]);

  return unreadCount;
};
